import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, WarningCircle, CircleNotch, ListPlus, ArrowsClockwise, Trash } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { useGenres, GenreOptions } from "@/lib/genres";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-[#111] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-amber";

// Whole YouTube playlist → one Primary Genre, straight to the live genre row.
export const PlaylistImportPanel = ({ headers }) => {
  const { groups, label } = useGenres();
  const [url, setUrl] = useState("");
  const [genre, setGenre] = useState("");
  const [fullOnly, setFullOnly] = useState(true);
  const [saveSync, setSaveSync] = useState(false);
  const [syncKey, setSyncKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const run = async () => {
    setBusy(true);
    setRes({ type: "info", msg: "Fetching the playlist and checking every video can be embedded…" });
    try {
      const { data } = await axios.post(`${API}/admin/import/youtube-playlist`, { url, genre, full_only: fullOnly, save_sync: saveSync }, { headers, withCredentials: true, timeout: 180000 });
      setRes({ type: "ok", msg: `Imported ${data.imported} of ${data.in_playlist} videos into ${label(data.genre)}.${data.saved_for_sync ? " Saved for weekly auto-sync." : ""}`, data });
      if (data.saved_for_sync) setSyncKey((k) => k + 1);
    } catch (e) {
      setRes({ type: "err", msg: e.response?.data?.detail || "Playlist import failed" });
    } finally {
      setBusy(false);
    }
  };

  const tone = res?.type === "ok" ? "border-amber/40 text-amber" : res?.type === "err" ? "border-crimson/50 text-crimson" : "border-white/20 text-white/70";
  const Icon = res?.type === "ok" ? CheckCircle : res?.type === "err" ? WarningCircle : CircleNotch;
  return (
    <section data-testid="playlist-import-panel" className="p-6 sm:p-8">
      <div />
      <p className="mb-5 text-sm text-white/50">
        Paste any public playlist link. Videos that YouTube won't let other sites play (like "Free with ads" movies), private or US-blocked videos are skipped automatically.
      </p>
      <div className="grid gap-3 md:grid-cols-[1fr_220px]">
        <input data-testid="playlist-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/playlist?list=PL…" className={field} />
        <select data-testid="playlist-genre-select" value={genre} onChange={(e) => setGenre(e.target.value)} className={field}>
          <option value="">Primary genre…</option>
          <GenreOptions groups={groups} />
        </select>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <label className="flex cursor-pointer items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-white/65">
          <input type="checkbox" data-testid="playlist-full-only" checked={fullOnly} onChange={(e) => setFullOnly(e.target.checked)} className="h-4 w-4 accent-amber" />
          Full-length only (20+ min)
        </label>
        <label className="flex cursor-pointer items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-white/65">
          <input type="checkbox" data-testid="playlist-save-sync" checked={saveSync} onChange={(e) => setSaveSync(e.target.checked)} className="h-4 w-4 accent-amber" />
          Save Playlist for Weekly Auto-Sync
        </label>
        <button type="button" data-testid="playlist-import-btn" onClick={run} disabled={busy || !url.trim() || !genre}
          className="ml-auto flex items-center gap-2 bg-amber px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors duration-300 hover:bg-amber/80 disabled:opacity-40">
          <ListPlus size={16} weight="bold" /> {busy ? "Importing…" : "Import Playlist"}
        </button>
      </div>
      {!fullOnly && <p className="mt-2 font-mono text-[10px] text-white/35">Videos under 20 min will land in the Shorts hub.</p>}
      {res && (
        <div data-testid="playlist-import-status" className={`mt-4 border px-4 py-3 font-mono text-xs ${tone}`}>
          <p className="flex items-center gap-2"><Icon size={18} weight="duotone" className={res.type === "info" ? "animate-spin" : ""} /> {res.msg}</p>
          {res.data && (
            <p data-testid="playlist-import-breakdown" className="mt-1.5 text-white/45">
              Skipped: {res.data.not_embeddable} not embeddable · {res.data.unavailable} private/blocked/removed · {res.data.too_short} too short
            </p>
          )}
        </div>
      )}
      <ActivePlaylistSyncs key={syncKey} headers={headers} />
    </section>
  );
};

const ActivePlaylistSyncs = ({ headers }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const { label } = useGenres();
  const load = () => axios.get(`${API}/admin/playlist-syncs`, { headers, withCredentials: true }).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const act = async (pid, kind) => {
    if (kind === "stop" && !window.confirm(`Stop weekly auto-sync for ${pid}? Already imported films stay.`)) return;
    setBusy(`${kind}-${pid}`);
    try {
      if (kind === "stop") {
        await axios.delete(`${API}/admin/playlist-syncs/${pid}`, { headers, withCredentials: true });
        toast.success("Auto-sync stopped");
      } else {
        const { data } = await axios.post(`${API}/admin/playlist-syncs/${pid}/run`, {}, { headers, withCredentials: true, timeout: 180000 });
        if (data.error) toast.error(data.error);
        else toast.success(`${data.pulled} new film${data.pulled === 1 ? "" : "s"} added · ${data.already_in_vault} already in the vault`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid="active-playlist-syncs" className="mt-8 border-t border-white/10 pt-6">
      <h3 className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-white/60">
        <ArrowsClockwise size={16} weight="bold" className="text-amber" /> Active Playlist Syncs
        <span className="text-white/30">· every Monday 4am ET</span>
      </h3>
      <ul className="mt-3">
        {(items || []).map((s) => (
          <li key={s.playlist_id} data-testid={`playlist-sync-${s.playlist_id}`} className="flex flex-wrap items-center gap-3 border-b border-white/5 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-xs text-white/85">{s.playlist_id}</p>
              <p className="font-mono text-[10px] text-white/40">
                {label(s.genre)}{s.full_only ? " · 20+ min" : " · any length"} · {s.last_run_at ? `last run ${new Date(s.last_run_at).toLocaleString()} · ${s.last_result?.error || `${s.last_result?.pulled ?? 0} new`}` : "not run yet"}
              </p>
            </div>
            <button type="button" data-testid={`playlist-sync-run-${s.playlist_id}`} disabled={!!busy} onClick={() => act(s.playlist_id, "run")}
              className="border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white/70 hover:border-amber hover:text-amber disabled:opacity-40">
              {busy === `run-${s.playlist_id}` ? "Syncing…" : "Sync now"}
            </button>
            <button type="button" data-testid={`playlist-sync-stop-${s.playlist_id}`} disabled={!!busy} onClick={() => act(s.playlist_id, "stop")}
              className="flex items-center gap-1.5 border border-crimson/50 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-crimson hover:bg-crimson hover:text-white disabled:opacity-40">
              <Trash size={12} weight="bold" /> Stop Sync
            </button>
          </li>
        ))}
        {items && !items.length && <li className="py-3 font-mono text-[11px] text-white/35">No playlists saved yet — tick "Save Playlist for Weekly Auto-Sync" when importing.</li>}
      </ul>
    </div>
  );
};
